import { n as RepositoryDiscoveryService, t as IGNORED_DIRECTORY_NAMES } from "./common_paths-B0_4d8JX.mjs";
import { n as GitLsFiles, r as isUnsupportedDircacheError, t as GitConfigCommand } from "./git_config-CLNchKNV.mjs";
import { Injectable } from "@gitlab/needle";
import { convertToHttpUrl } from "@gitlab-org/core";
import { promises } from "node:fs";
import path, { dirname, join } from "node:path";
import { sortBy } from "lodash-es";
import { grepQueryBuilder, pathspec, simpleGit } from "simple-git";
import { Logger, withPrefix } from "@gitlab-org/logging";
import { URI } from "vscode-uri";
import { FsClient, isFileSchemeUri } from "@gitlab-org/fs";
import { ConfigService } from "@gitlab-org/config";
import { SecretRedactor } from "@gitlab-org/secret-redaction";
import * as git from "isomorphic-git";
import { spawn } from "node:child_process";
import { rm, writeFile } from "node:fs/promises";
import { tmpdir } from "node:os";
import { v4 } from "uuid";
import { readFileSync } from "fs";
import { homedir } from "os";
import { join as join$1 } from "path";
import SSHConfig from "ssh-config";
import { parse } from "shell-quote";
//#region src/default_stateless_repository.ts
const STATUS_CODE_MAP = {
	M: "modified",
	A: "added",
	D: "deleted",
	R: "renamed",
	C: "copied",
	U: "conflicted"
};
var DefaultStatelessRepository = class {
	#git;
	constructor(fsPath) {
		this.fsPath = fsPath;
		this.#git = simpleGit(fsPath);
	}
	async listRemotes() {
		try {
			return (await this.#git.getRemotes(true)).map((remote) => ({
				remote: remote.name,
				url: remote.refs.fetch || remote.refs.push || ""
			}));
		} catch (error) {
			throw new Error(`Failed to list remotes: ${error}`, { cause: error });
		}
	}
	async getCurrentCommit() {
		try {
			return (await this.#git.revparse(["HEAD"])).trim();
		} catch (error) {
			if (error instanceof Error && error.message.includes("unknown revision")) return null;
			throw new Error(`Failed to get current commit: ${error}`, { cause: error });
		}
	}
	async getCurrentBranchName() {
		try {
			return (await this.#git.raw(["branch", "--show-current"])).trim() || null;
		} catch (error) {
			throw new Error(`Failed to get current branch name: ${error}`, { cause: error });
		}
	}
	async checkIgnore(filePaths) {
		return this.#git.checkIgnore(filePaths);
	}
	async isInsideWorkTree() {
		try {
			return (await this.#git.raw(["rev-parse", "--is-inside-work-tree"])).trim() === "true";
		} catch (error) {
			if (error instanceof Error && error.message.includes("must be run in a work tree")) return false;
			throw new Error(`Failed to check if inside work tree: ${error}`, { cause: error });
		}
	}
	async getFiles(glob) {
		try {
			const args = ["ls-files"];
			if (glob) {
				const patterns = Array.isArray(glob) ? glob : [glob];
				args.push(...patterns);
			}
			const trackedFiles = await this.#git.raw(args);
			const untrackedArgs = [
				"ls-files",
				"--others",
				"--exclude-standard"
			];
			if (glob) {
				const patterns = Array.isArray(glob) ? glob : [glob];
				untrackedArgs.push(...patterns);
			}
			const untrackedFiles = await this.#git.raw(untrackedArgs);
			const allFiles = [...trackedFiles.split("\n").filter(Boolean), ...untrackedFiles.split("\n").filter(Boolean)];
			return [...new Set(allFiles)];
		} catch (error) {
			throw new Error(`Failed to get files: ${error}`, { cause: error });
		}
	}
	async grep(params) {
		const options = {
			"--untracked": null,
			paths: pathspec(params.searchDirectory)
		};
		if (params.caseInsensitive) options["-i"] = null;
		Object.entries({
			maxDepth: "--max-depth",
			context: "--context",
			maxCountFile: "--max-count"
		}).forEach(([key, flag]) => {
			const value = params?.[key];
			if (value !== void 0) options[flag] = value;
		});
		return this.#git.grep(grepQueryBuilder(...params.searchQuery), options);
	}
	async getStatus() {
		try {
			const status = await this.#git.status();
			const files = [];
			for (const file of status.files) if (file.index === "?" && file.working_dir === "?") files.push({
				path: file.path,
				status: "untracked",
				staged: false,
				from: file.from
			});
			else {
				const indexStatus = STATUS_CODE_MAP[file.index];
				const workingDirStatus = STATUS_CODE_MAP[file.working_dir];
				if (indexStatus) files.push({
					path: file.path,
					status: indexStatus,
					staged: true,
					from: file.from
				});
				if (workingDirStatus) files.push({
					path: file.path,
					status: workingDirStatus,
					staged: false,
					from: file.from
				});
			}
			return {
				files,
				branch: status.current,
				tracking: status.tracking,
				ahead: status.ahead,
				behind: status.behind,
				isClean: status.isClean()
			};
		} catch (error) {
			throw new Error(`Failed to get git status: ${error}`, { cause: error });
		}
	}
};
//#endregion
//#region src/find_git_repo_root.ts
/**
* Find the git repository root for `startDir`: the nearest ancestor (inclusive)
* that contains a `.git` entry. A `.git` FILE (git worktree / submodule pointer)
* counts as a hit, not just a `.git` directory.
*
* This is a pure filesystem walk (a `stat` per ancestor) — it never spawns a
* git subprocess.
*
* @returns the directory containing `.git`, or `undefined` when no `.git` entry
*   exists anywhere up the tree.
*/
async function findGitRepoRoot(startDir) {
	let current = startDir;
	while (true) {
		if (await pathExists(join(current, ".git"))) return current;
		const parent = dirname(current);
		if (parent === current) return;
		current = parent;
	}
}
async function pathExists(p) {
	try {
		await promises.stat(p);
		return true;
	} catch {
		return false;
	}
}
//#endregion
//#region src/stateless_repository_discovery_service.ts
let _initClass$2;
function _applyDecs$2(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$2(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$2(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$2(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$2(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$2(t) {
	var i = _toPrimitive$2(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$2(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$2(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$2(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const MAX_REPO_DEPTH = 2;
let _StatelessRepositoryD;
(class {
	static {
		[_StatelessRepositoryD, _initClass$2] = _applyDecs$2(this, [Injectable(RepositoryDiscoveryService, [Logger])], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[StatelessRepositoryDiscoveryService]");
	}
	async getMatchingRepository(filePath, workspacePath) {
		const repositories = await this.getRepositoriesForWorkspace(workspacePath);
		let bestMatch;
		for (const repository of repositories) {
			const repositoryPath = repository.fsPath;
			if (filePath === repositoryPath || filePath.startsWith(repositoryPath + path.sep)) {
				if (repositoryPath.length > (bestMatch?.fsPath.length ?? 0)) bestMatch = repository;
			}
		}
		return bestMatch;
	}
	async getRepositoriesForWorkspace(workspacePath) {
		try {
			const validationPromises = (await this.#findDotGitFiles(workspacePath)).map(async (dotGitFile) => {
				const repositoryPath = path.dirname(dotGitFile);
				return {
					repositoryPath,
					isValid: await this.#isValidRepository(repositoryPath)
				};
			});
			return sortBy((await Promise.all(validationPromises)).filter(({ isValid }) => isValid).map(({ repositoryPath }) => new DefaultStatelessRepository(repositoryPath)), (r) => r.fsPath);
		} catch (error) {
			this.#logger.debug(`Failed to scan workspace ${workspacePath}`, error);
			return [];
		}
	}
	async getRepositoriesForWorkspaces(workspaceFolders) {
		const result = /* @__PURE__ */ new Map();
		await Promise.all(workspaceFolders.filter((folder) => {
			if (!isFileSchemeUri(folder.uri)) {
				this.#logger.debug(`Skipping non-file workspace folder: ${folder.uri}`);
				return false;
			}
			return true;
		}).map(async (folder) => {
			const folderPath = URI.parse(folder.uri).fsPath;
			const repositories = await this.getRepositoriesForWorkspace(folderPath);
			for (const repo of repositories) result.set(repo, folder);
		}));
		return result;
	}
	async findRepositoryRoot(directoryPath) {
		const repoRoot = await findGitRepoRoot(directoryPath);
		if (repoRoot === void 0) {
			this.#logger.debug(`No git repository found for "${directoryPath}"`);
			return;
		}
		return promises.realpath(repoRoot);
	}
	async #findDotGitFiles(rootPath, currentDepth = 0) {
		try {
			const entries = await promises.readdir(rootPath, { withFileTypes: true });
			const directories = entries.filter((entry) => entry.isDirectory()).map((entry) => ({
				name: entry.name,
				fullPath: path.join(rootPath, entry.name)
			}));
			const gitCandidates = entries.filter(({ name }) => name === ".git").map(({ name }) => path.join(rootPath, name));
			if (currentDepth >= MAX_REPO_DEPTH) return gitCandidates;
			const recursivePromises = directories.filter(({ name }) => name !== ".git" && !IGNORED_DIRECTORY_NAMES.includes(name)).map(({ fullPath }) => fullPath).map((subDir) => this.#findDotGitFiles(subDir, currentDepth + 1));
			const recursiveResults = await Promise.all(recursivePromises);
			return [...gitCandidates, ...recursiveResults.flat()];
		} catch (error) {
			this.#logger.debug(`Failed to read directory ${rootPath}`, error);
			return [];
		}
	}
	async #isValidRepository(repositoryPath) {
		try {
			return await simpleGit(repositoryPath).checkIsRepo();
		} catch (error) {
			this.#logger.debug(`Invalid repository at ${repositoryPath}`, error);
			return false;
		}
	}
	static {
		_initClass$2();
	}
});
//#endregion
//#region src/git/commands/base_git_command.ts
var BaseGitCommand = class {
	#configService;
	#secretRedactor;
	constructor(logger, configService, secretRedactor) {
		this.logger = logger;
		this.#configService = configService;
		this.#secretRedactor = secretRedactor;
	}
	async buildGitArgs(command, commandArgs, repositoryUrl, basePath, gitDir) {
		const defaultArgs = await this.defaultGitArgs(repositoryUrl, basePath);
		const gitDirArgs = [];
		if (gitDir) gitDirArgs.push("--git-dir", gitDir, "--work-tree", basePath);
		return [
			...defaultArgs,
			...gitDirArgs,
			command,
			...commandArgs
		];
	}
	async defaultGitArgs(repositoryUrl, basePath) {
		const args = [];
		const gitHttpsUser = this.#configService.get("gitHttpUser") || "auth";
		const gitUserEmail = this.#configService.get("gitUserEmail") || "";
		const gitUserName = this.#configService.get("gitUserName") || "";
		let baseURL;
		try {
			baseURL = this.extractBaseURL(repositoryUrl);
		} catch (error) {
			this.logger.error(`Failed to get baseURL for git repo from repositoryUrl "${repositoryUrl}"`, error);
			throw error;
		}
		if (baseURL.length) {
			if (gitHttpsUser) {
				const credentialHelper = `!f() { echo username=${gitHttpsUser}; echo password=$GIT_PASSWORD; }; f`;
				args.push(`-c`, `credential.helper=${credentialHelper}`);
			}
			const ciRepoBaseURL = this.#extractCIRepositoryBaseURL();
			if (ciRepoBaseURL) args.push(`-c`, `url.${baseURL}/.insteadOf=${ciRepoBaseURL}`);
			else {
				const domain = baseURL.replace(/^https?:\/\//, "");
				args.push(`-c`, `url.${baseURL}/.insteadOf=git@${domain}:`);
			}
		}
		args.push(`-c`, `safe.directory=${basePath}`);
		args.push(`-c`, `user.email=${gitUserEmail}`);
		if (gitUserName) args.push("-c", `user.name=${gitUserName}`);
		return args;
	}
	extractBaseURL(url) {
		if (url === "") return "";
		try {
			const httpUrl = convertToHttpUrl(url);
			const parsedUrl = new URL(httpUrl);
			if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") throw new Error(`unsupported protocol: ${parsedUrl.protocol}`);
			if (!parsedUrl.origin) throw new Error(`invalid URL format: ${url}`);
			return parsedUrl.origin;
		} catch (error) {
			throw new Error(`invalid URL: ${url}, ${error instanceof Error ? error.message : "parsing failed"}`);
		}
	}
	/**
	* Extracts the base URL from CI_REPOSITORY_URL for GitLab CI authentication compatibility.
	*
	* When workflows run in GitLab CI pipelines, the CI environment automatically sets CI_REPOSITORY_URL
	* with an embedded CI job token (e.g., `http://gitlab-ci-token:TOKEN@gdk.test:3000/project.git`).
	*
	* The workflow executor uses an OAuth token with `ai_features` scope for git operations.
	* This OAuth token cannot authenticate using the CI token-embedded URL because:
	* 1. The git remote URL is configured with the CI job token credentials
	* 2. The OAuth token has different permissions and cannot use the CI token auth mechanism
	* 3. Attempting to push results in: "You are not allowed to push code to this project" (HTTP 403)
	*
	* So we use Git's URL rewriting feature (`url.<base>.insteadOf=<other>`) to transparently replace
	* the CI token-embedded URL with the clean base URL. This allows authentication to happen via
	* GIT_ASKPASS using the OAuth token instead of the embedded CI token.
	*
	* **Example:**
	* - Input: `http://gitlab-ci-token:foo-token-bar@gdk.test:3000/gitlab-duo/project.git`
	* - Output: `http://gitlab-ci-token:foo-token-bar@gdk.test:3000`
	* - Git config: `url.https://gitlab.com/.insteadOf=http://gitlab-ci-token:foo-token-bar@gdk.test:3000`
	*/
	#extractCIRepositoryBaseURL() {
		const ciRepositoryUrl = process.env.CI_REPOSITORY_URL;
		if (!ciRepositoryUrl) return null;
		try {
			const parsedUrl = new URL(ciRepositoryUrl);
			let baseUrl = `${parsedUrl.protocol}//${parsedUrl.host}`;
			if (parsedUrl.username || parsedUrl.password) {
				const userInfo = parsedUrl.password ? `${parsedUrl.username}:${parsedUrl.password}` : parsedUrl.username;
				baseUrl = `${parsedUrl.protocol}//${userInfo}@${parsedUrl.host}`;
			}
			return baseUrl;
		} catch (error) {
			throw new Error(`Failed to parse CI_REPOSITORY_URL, does it contain the expected structure? ${error instanceof Error ? error.message : "parsing failed"}`);
		}
	}
	/**
	* @deprecated No longer needed - credential helper is now inline in git args.
	* Kept for backward compatibility with existing callers.
	*/
	async createGitAskPass() {
		const tempFilePath = join(tmpdir(), `git-askpass-${v4()}`);
		await writeFile(tempFilePath, "#!/bin/sh\necho $GIT_PASSWORD", { mode: 448 });
		return tempFilePath;
	}
	/**
	* @deprecated No longer needed - credential helper is now inline in git args.
	* Kept for backward compatibility with existing callers.
	*/
	async removeGitAskPass(tempFilePath) {
		if (!tempFilePath) return;
		try {
			await rm(tempFilePath, { force: true });
		} catch (cleanupError) {
			this.logger.warn(`Failed to clean up temporary git askpass file: "${tempFilePath}"`, cleanupError);
		}
	}
	runGitCommand(gitArgs, basePath, gitPassword, abortSignal) {
		const redactedGitArgs = this.#secretRedactor.redactSecrets(JSON.stringify(gitArgs), "git-command-args");
		this.logger.debug(`Running git command with the following argument: ${redactedGitArgs}`);
		return new Promise((resolve, reject) => {
			const child = spawn("git", gitArgs, {
				shell: false,
				cwd: basePath,
				env: {
					PATH: process.env.PATH,
					HOME: process.env.HOME || process.env.USERPROFILE,
					GIT_EXEC_PATH: process.env.GIT_EXEC_PATH,
					GIT_PASSWORD: this.#configService.get("gitHttpPassword") || gitPassword,
					GIT_TERMINAL_PROMPT: "0",
					GIT_CONFIG_NOSYSTEM: "1",
					GIT_CONFIG_NOGLOBAL: "1",
					GIT_CONFIG_GLOBAL: "/dev/null",
					GIT_AUTHOR_NAME: this.#configService.get("gitAuthorName") || "",
					GIT_AUTHOR_EMAIL: this.#configService.get("gitAuthorEmail") || "",
					GIT_COMMITTER_NAME: this.#configService.get("gitUserName") || "",
					GIT_COMMITTER_EMAIL: this.#configService.get("gitUserEmail") || "",
					http_proxy: process.env.http_proxy || process.env.HTTP_PROXY,
					https_proxy: process.env.https_proxy || process.env.HTTPS_PROXY,
					all_proxy: process.env.all_proxy || process.env.ALL_PROXY,
					no_proxy: process.env.no_proxy || process.env.NO_PROXY
				},
				signal: abortSignal
			});
			let combinedOutput = "";
			child.stdout.on("data", (chunk) => {
				combinedOutput += chunk;
			});
			child.stderr.on("data", (chunk) => {
				combinedOutput += chunk;
			});
			child.on("error", (err) => {
				this.logger.debug("Git command error spawning process", { error: err.message });
				reject(err);
			});
			child.on("close", (code) => {
				if (code !== 0) this.logger.debug(`Git command exited with code ${code}`);
				resolve({
					output: combinedOutput,
					exitCode: code
				});
			});
		});
	}
};
//#endregion
//#region src/git/commands/ls_files.node.ts
let _initClass$1;
function _applyDecs$1(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$1(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$1(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$1(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$1(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$1(t) {
	var i = _toPrimitive$1(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$1(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$1(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$1(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
let _NodeGitLsFiles;
(class extends BaseGitCommand {
	static {
		[_NodeGitLsFiles, _initClass$1] = _applyDecs$1(this, [Injectable(GitLsFiles, [
			Logger,
			ConfigService,
			FsClient,
			SecretRedactor
		])], [], 0, void 0, BaseGitCommand).c;
	}
	#fsClient;
	constructor(logger, configService, fsClient, secretRedactor) {
		super(withPrefix(logger, "[NodeGitLsFiles]"), configService, secretRedactor);
		this.#fsClient = fsClient;
	}
	async execute(basePath, repositoryUrl = "", gitPassword = "", gitDir) {
		try {
			return await git.listFiles({
				dir: basePath,
				fs: this.#fsClient,
				gitdir: gitDir
			});
		} catch (err) {
			if (isUnsupportedDircacheError(err)) this.logger.debug(`Repository "${basePath}" has an unsupported dircache version configured. "isomorphic-git" cannot work with anything other than dircache "2". Falling back to direct system git call`);
		}
		try {
			const gitArgs = await this.buildGitArgs("ls-files", [], repositoryUrl, basePath, gitDir);
			const result = await this.runGitCommand(gitArgs, basePath, gitPassword);
			if (result.exitCode !== 0) throw new Error(`git command failed with exit code ${result.exitCode}: ${result.output}`);
			this.logger.debug("Command executed successfully");
			return result.output.split("\n").filter((file) => file.trim() !== "");
		} catch (error) {
			this.logger.error("Command execution error", error);
			throw error;
		}
	}
	static {
		_initClass$1();
	}
});
//#endregion
//#region src/git/commands/config.node.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
let _NodeGitConfigCommand;
(class {
	static {
		[_NodeGitConfigCommand, _initClass] = _applyDecs(this, [Injectable(GitConfigCommand, [Logger])], []).c;
	}
	#logger;
	constructor(logger) {
		this.#logger = withPrefix(logger, "[NodeGitConfigCommand]");
	}
	async getRemoteUrl(_gitConfigUri, _remoteName, fallbackUrl) {
		try {
			const hostname = this.#extractHostname(fallbackUrl);
			if (!hostname) return fallbackUrl;
			const resolvedHostname = this.#resolveSSHHostname(hostname);
			if (resolvedHostname !== hostname) {
				this.#logger.debug(`Resolved SSH hostname: '${hostname}' -> '${resolvedHostname}'`);
				if (fallbackUrl.startsWith("git@")) return fallbackUrl.replace(`git@${hostname}:`, `git@${resolvedHostname}:`);
				try {
					const url = new URL(fallbackUrl);
					url.hostname = resolvedHostname;
					return url.toString();
				} catch {
					return fallbackUrl.replace(hostname, resolvedHostname);
				}
			}
			return fallbackUrl;
		} catch {
			return fallbackUrl;
		}
	}
	#extractHostname(url) {
		try {
			const gitMatch = url.match(/^git@([^:]+):/);
			if (gitMatch) return gitMatch[1] ?? null;
			return new URL(url).hostname;
		} catch {
			return null;
		}
	}
	#resolveSSHHostname(hostname) {
		try {
			const configContent = readFileSync(join$1(homedir(), ".ssh", "config"), "utf8");
			const computed = SSHConfig.parse(configContent).compute(hostname);
			if (computed.HostName) return computed.HostName;
			return hostname;
		} catch {
			return hostname;
		}
	}
	static {
		_initClass();
	}
});
//#endregion
//#region src/git/commands/contributions.node.ts
const nodeGitCommandsContributions = [_NodeGitLsFiles, _NodeGitConfigCommand];
//#endregion
//#region src/git/utils/git_command_validator.ts
const MAX_ARGS_LENGTH = 1e3;
const MAX_ARGS_STR_TOTAL_BYTES = process.platform === "win32" ? 3e4 : 1e6;
function parseGitArgs(argsString) {
	return parse(argsString).map((entry) => {
		if (typeof entry === "string") return entry;
		if (typeof entry === "object" && entry !== null) {
			if ("pattern" in entry) return entry.pattern;
			if ("op" in entry) return entry.op;
		}
		return String(entry);
	});
}
function isArgLengthValid(arg) {
	return arg.length <= MAX_ARGS_LENGTH;
}
function isCommandStrLengthValid(command, argsString) {
	return Buffer.from(command).length + Buffer.from(argsString).length < MAX_ARGS_STR_TOTAL_BYTES;
}
function validateGitCommand(command, argsString = "") {
	if (!isCommandStrLengthValid(command, argsString)) return {
		isValid: false,
		error: `Command is too long: bytes exceed ${MAX_ARGS_STR_TOTAL_BYTES} byte limit on ${process.platform}`
	};
	let args;
	try {
		args = parseGitArgs(argsString);
	} catch (error) {
		return {
			isValid: false,
			error: error instanceof Error ? error.message : "Failed to parse arguments"
		};
	}
	for (const arg of args) if (!isArgLengthValid(arg)) return {
		isValid: false,
		error: `Argument too long: ${arg.substring(0, 50)}...`
	};
	return {
		isValid: true,
		args
	};
}
//#endregion
export { BaseGitCommand, DefaultStatelessRepository, _NodeGitLsFiles as NodeGitLsFiles, _StatelessRepositoryD as StatelessRepositoryDiscoveryService, findGitRepoRoot, nodeGitCommandsContributions, validateGitCommand };

//# sourceMappingURL=node.mjs.map